/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-invalid-this */
import { combineEpics, Epic, StateObservable } from 'redux-observable';
import { Observable, observableOf, rx } from '@shared/rx';
import { get, has, isFunction, set } from 'lodash';
import { Injectable } from '@angular/core';
import {
  IAction,
  IApiAction,
  IApiListResponse,
  IDocumentOperationOptions,
  IEpicsService,
  IFieldPreferences,
  StringObject,
} from '@shared/types';
import { IAppState } from '@app/store';
import {
  EntityServiceProvider,
  UtilityServiceProvider,
} from '@shared/services';
// import actions
import {
  APIActions,
  BULK_UPDATE,
  DELETE_DOCUMENT_BY_ENTITY_ID,
  FETCH_CREATE_ENTITY,
  FETCH_GET_DOCUMENT_BY_ID,
  FETCH_LIST,
  FETCH_LIST_SUCCESS,
  FETCH_SCHEMA,
  FETCH_UPDATE_ENTITY,
  RouterActions,
  SHOW_DOCUMENT,
} from '@store/actions';
import { fromPromise } from 'rxjs/internal-compatibility';

@Injectable()
export class ApiEpics implements IEpicsService {
  public combinedEpic: Function = combineEpics(
    // used
    this.fetchList.bind(this),
    this.bulkUpdate.bind(this),
    this.getEntityCaptions.bind(this),
    this.fetchSchema.bind(this),
    this.getDocumentById.bind(this),
    this.deleteDocumentByEntityId.bind(this),
    this.showDocument.bind(this),
    this.createEntity.bind(this),
    this.updateEntity.bind(this),
  );

  public constructor(
    private apiActions: APIActions,
    private entityServiceProvider: EntityServiceProvider,
    private utilityServiceProvider: UtilityServiceProvider,
  ) {}

  public getEpic(): Epic<IAction, IAction, IAppState> {
    const epicFn = (
      inputActions$: Observable<IAction>,
      store: StateObservable<IAppState>,
    ): Observable<IAction> => {
      const actions$ = inputActions$.pipe(
        rx.filter(
          (action: IAction) =>
            !!get(action, 'payload.entity_type') ||
            !!get(action, 'payload.service_name'),
        ),
        rx.map(this.attachService),
        rx.filter((action: IAction) => !!get(action, ['service'], false)),
        rx.share(),
      );

      return this.combinedEpic(actions$, store);
    };

    return epicFn as unknown as Epic<IAction, IAction, IAppState>;
  }

  // method for attaching injected entityService to actions payload by entity_type
  private attachService = (action: IAction): IApiAction => {
    return has(action, 'payload.entity_type') &&
      !has(action, 'payload.service_name')
      ? this.attachEntityService(action)
      : this.attachUtilityService(action);
  };

  // method for attaching injected entityService to actions payload by entity_type
  private attachEntityService = (action: IAction): IApiAction => {
    // finding the class of service by entity_type from payload
    // returning the copied action which was extended by entity service
    const entityType = action.payload.entity_type;

    return Object.assign(
      {},
      {
        ...action,
        service: this.entityServiceProvider.getService(entityType),
      },
    );
  };

  // method for attaching injected utilityService to actions payload by entity_type
  private attachUtilityService = (action: IAction): IApiAction => {
    // finding the class of service by entity_type from payload
    // returning the copied action which was extended by entity service
    const serviceName = get(action, 'payload.service_name', 'XprtDataService');

    return Object.assign(
      {},
      {
        ...action,
        service: this.utilityServiceProvider.getUtilityService(serviceName),
      },
    );
  };

  /**
   * Method for catching FETCH_LIST action type for current entity
   * and calling entity's service method
   *
   * @param action$ - action Observable
   * @param store - store
   * @returns
   */
  private fetchList(
    action$: Observable<IApiAction>,
    store: StateObservable<IAppState>,
  ): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) => [FETCH_LIST].includes(action.type)),
      rx.mergeMap((action: IApiAction) => {
        const entityType = action.payload.entity_type;
        const beforeFetchListData = get(
          action.service,
          'config.list.beforeFetchListData',
        );

        return fromPromise(
          new Promise((resolve): void => {
            return isFunction(beforeFetchListData)
              ? resolve(beforeFetchListData(action.service))
              : resolve(null);
          }),
        ).pipe(
          rx.catchError(() => observableOf(null)),
          rx.switchMap(() =>
            action.service
              .fetchList(action.payload.query, action.payload.options)
              .pipe(
                rx.map((response) => {
                  const afterFetchListFunc = get(
                    action.service,
                    'config.list.afterFetchListData',
                  );

                  if (isFunction(afterFetchListFunc)) {
                    const mappedItems = afterFetchListFunc(
                      get(response, 'items', []),
                      {
                        originResponse: response,
                        dependentEntitiesData: get(
                          action,
                          ['payload', 'options', 'dependentEntitiesData'],
                          {},
                        ),
                      },
                    );

                    // update response items with mapped items from afterFetchListData callback
                    set(response, 'items', mappedItems);
                  }

                  return this.apiActions.fetchListSuccessAction(
                    entityType,
                    response,
                    action.payload,
                    action.payload.options,
                  );
                }),
                rx.catchError((errors) =>
                  observableOf(
                    this.apiActions.fetchListErrorAction(
                      entityType,
                      errors,
                      action.payload,
                      action.payload.options,
                    ),
                  ),
                ),
              ),
          ),
        );
      }),
    ) as any;
  }

  /**
   * Method for catching FETCH_LIST_SUCCESS action type for current entity
   * and extracting captions from payload
   *
   * @param action$ - action Observable
   * @returns
   */
  private getEntityCaptions(
    action$: Observable<IApiAction>,
  ): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) => [FETCH_LIST_SUCCESS].includes(action.type)),
      rx.mergeMap((action: IApiAction) => {
        const entityType = action.payload.entity_type;
        const items = get(action, 'payload.response.items', []);
        const itemCaptions: StringObject = {};

        items.forEach((item) => {
          itemCaptions[item.entity_id] = action.service.getItemText(item);
        });

        return observableOf(
          this.apiActions.getEntityCaptionsSuccessAction(
            entityType,
            itemCaptions,
          ),
        );
      }),
    );
  }

  /**
   * Method for catching BULK_UPDATE action type for current entity
   * and calling entity's service method
   *
   * @param action$ - action Observable
   * @param store - store
   * @returns
   */
  private bulkUpdate(
    action$: Observable<IApiAction>,
    store: StateObservable<IAppState>,
  ): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) => [BULK_UPDATE].includes(action.type)),
      rx.mergeMap((action: IApiAction) => {
        const entityType = action.payload.entity_type;

        return action.service
          .bulkUpdate(action.payload.documents, action.payload.options)
          .pipe(
            rx.map((response) =>
              this.apiActions.bulkUpdateSuccessAction(
                entityType,
                response,
                action.payload,
                action.payload.options,
              ),
            ),
            rx.catchError((errors) =>
              observableOf(
                this.apiActions.bulkUpdateErrorAction(
                  entityType,
                  errors,
                  action.payload,
                  action.payload.options,
                ),
              ),
            ),
          );
      }),
    ) as any;
  }

  /**
   * Method for catching FETCH_SCHEMA action type for current entity
   * and calling entity's service method
   *
   * @param action$ - action Observable
   * @returns
   */
  private fetchSchema(action$: Observable<IAction>): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) => [FETCH_SCHEMA].includes(action.type)),
      rx.mergeMap((action: IApiAction) => {
        const entityType = action.payload.entity_type;

        return action.service.fetchSchema().pipe(
          rx.map((schema: any) => {
            const entityIdProperties: IFieldPreferences = {
              omit: true,
              show: false,
              type: 'string',
              default: null,
            };
            const properties = Object.assign({}, schema.properties, {
              entity_id: entityIdProperties,
            });

            return Object.assign({}, schema, { properties });
          }),
          rx.map((schema) =>
            this.apiActions.fetchSchemaSuccessAction(entityType, schema),
          ),
          rx.catchError((errors) =>
            observableOf(
              this.apiActions.fetchSchemaErrorAction(entityType, errors),
            ),
          ),
        );
      }),
    );
  }

  /**
   * Method for catching FETCH_GET_DOCUMENT_BY_ID action type
   * and calling entity's service method
   *
   * @param action$ - action Observable
   * @returns
   */
  private getDocumentById(action$: Observable<IAction>): Observable<any> {
    return action$.pipe(
      rx.filter((action) => [FETCH_GET_DOCUMENT_BY_ID].includes(action.type)),
      rx.mergeMap((action: IApiAction) => {
        const entityType = action.payload.entity_type;
        const { id } = action.payload;
        const documentOperationOptions = {
          /* eslint-disable-next-line */
          ...(get(
            action,
            ['payload', 'options'],
            {},
          ) as IDocumentOperationOptions),
        };

        return action.service
          .getDocumentById(id, documentOperationOptions)
          .pipe(
            rx.map((response: IApiListResponse) =>
              this.apiActions.fetchGetDocumentByIdSuccessAction(
                entityType,
                response,
                action.payload.options,
              ),
            ),
            rx.catchError((errors) =>
              observableOf(
                this.apiActions.fetchGetDocumentByIdErrorAction(
                  entityType,
                  errors,
                  action.payload.options,
                ),
              ),
            ),
          );
      }),
    );
  }

  private showDocument(action$: Observable<IApiAction>): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) => [SHOW_DOCUMENT].includes(action.type)),
      rx.map((action: IApiAction) => {
        const uiRoute = get(
          action,
          'service.config.uiRoute',
          action.payload.entity_type,
        );
        const documentRoute = get(action, 'payload.document._id', 'add');

        return RouterActions.updateLocationAction(
          `${uiRoute}/${documentRoute}`,
        );
      }),
    );
  }

  /**
   * Method for catching DELETE_DOCUMENT_BY_ENTITY_ID action type for current entity
   * and calling entity's service method
   *
   * @param action$ - action Observable
   * @returns
   */
  private deleteDocumentByEntityId(
    action$: Observable<IAction>,
  ): Observable<IAction> {
    return action$.pipe(
      rx.filter((action) =>
        [DELETE_DOCUMENT_BY_ENTITY_ID].includes(action.type),
      ),
      rx.mergeMap((action: IApiAction) => {
        const entityType = action.payload.entity_type;
        const entityId = action.payload.entityId;

        return action.service.deleteDocumentByEntityId(entityId).pipe(
          rx.map((response: IApiListResponse) =>
            this.apiActions.deleteDocumentByEntityIdSuccessAction(
              entityType,
              response,
              action.payload.options,
            ),
          ),
          rx.catchError((errors) =>
            observableOf(
              this.apiActions.deleteDocumentByEntityIdErrorAction(
                entityType,
                errors,
                action.payload.options,
              ),
            ),
          ),
        );
      }),
    );
  }

  private createEntity(action$: Observable<IAction>): Observable<any> {
    return action$.pipe(
      rx.filter((action) => [FETCH_CREATE_ENTITY].includes(action.type)),
      rx.mergeMap((action: IApiAction) => {
        const entityType = action.payload.entity_type;

        return action.service
          .createEntity(action.payload.documents, action.payload.options)
          .pipe(
            rx.map((response: IApiListResponse) =>
              this.apiActions.createEntitySuccessAction(
                entityType,
                response,
                action.payload.options,
              ),
            ),
            rx.catchError((errorsObj) => {
              return observableOf(
                this.apiActions.createEntityErrorAction(
                  entityType,
                  errorsObj,
                  action.payload.options,
                ),
              );
            }),
          );
      }),
    );
  }

  private updateEntity(action$: Observable<IAction>): Observable<any> {
    return action$.pipe(
      rx.filter((action) => [FETCH_UPDATE_ENTITY].includes(action.type)),
      rx.mergeMap((action: IApiAction) => {
        const entityType = action.payload.entity_type;

        return action.service
          .updateEntity(action.payload.documents, action.payload.options)
          .pipe(
            rx.map((response: IApiListResponse) =>
              this.apiActions.updateEntitySuccessAction(
                entityType,
                response,
                action.payload.options,
              ),
            ),
            rx.catchError((errorsObj) => {
              return observableOf(
                this.apiActions.updateEntityErrorAction(
                  entityType,
                  errorsObj,
                  action.payload.options,
                ),
              );
            }),
          );
      }),
    );
  }
}
